import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Heading, Subheading } from '../../../../components/Heading';
import { Text } from '../../../../components/Text';
import { Button } from '../../../../components/Button';
import { Select } from '../../../../components/Select';
import { Divider } from '../../../../components/Divider';
import { Card } from '../../../../components/Card';
import Loading from '../../../../components/Loading';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { useAiReportHelper } from '../../../../hooks/useAiReportHelper';
import { AiReportConversion } from '../../../../models/AiReportHelper';

const CHART_COLORS = ['#7c3aed', '#0ea5e9', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#14b8a6', '#f43f5e'];

type ChartType = 'bar' | 'line' | 'pie';
type Aggregation = 'sum' | 'count';
type GroupBy = 'day' | 'week' | 'month' | 'year';

const AnalyticsChartPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { fetchById, getAnalyticsData, loading } = useAiReportHelper();

    const [conversion, setConversion] = useState<AiReportConversion | null>(null);
    const [xColumn, setXColumn] = useState('');
    const [yColumn, setYColumn] = useState('');
    const [aggregation, setAggregation] = useState<Aggregation>('count');
    const [chartType, setChartType] = useState<ChartType>('bar');
    const [groupBy, setGroupBy] = useState<GroupBy>('month');
    const [chartData, setChartData] = useState<{ name: string; value: number }[] | null>(null);
    const [isDateSeries, setIsDateSeries] = useState(false);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        const load = async () => {
            if (!id) return;
            try {
                const res = await fetchById(Number(id));
                setConversion(res.conversion);
                const headers = res.conversion.source_headers || [];
                if (headers.length > 0) setXColumn(headers[0]);
            } catch (err: any) {
                showErrorToast('Failed to load this report');
            }
        };
        load();
    }, [id]);

    useEffect(() => {
        if (!yColumn) setAggregation('count');
    }, [yColumn]);

    const handleGenerate = async () => {
        if (!id || !xColumn) return;
        setGenerating(true);
        try {
            const res = await getAnalyticsData(Number(id), {
                xColumn,
                yColumn: yColumn || undefined,
                aggregation,
                groupBy,
            });
            setChartData(res.labels.map((label, i) => ({ name: label, value: res.values[i] })));
            setIsDateSeries(res.isDate);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to generate chart');
        } finally {
            setGenerating(false);
        }
    };

    if (loading && !conversion) return <div className="flex justify-center py-20"><Loading /></div>;
    if (!conversion) return <Text>Report not found.</Text>;

    const headers = conversion.source_headers || [];

    return (
        <div className="mx-auto max-w-5xl">
            <Link
                to="/gp/ai-report-helper"
                className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
            >
                <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                Back to AI Report Helper
            </Link>
            <Heading className="mt-4">Analytics</Heading>
            <Text className="mt-1">
                {conversion.title || conversion.source_file_name} · {conversion.source_row_count ?? 0} rows
            </Text>
            <Card className="mt-6 p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                <div>
                    <Subheading>X axis</Subheading>
                    <Select className="mt-2" value={xColumn} onChange={(e) => setXColumn(e.target.value)}>
                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </Select>
                </div>
                <div>
                    <Subheading>Y axis</Subheading>
                    <Select className="mt-2" value={yColumn} onChange={(e) => setYColumn(e.target.value)}>
                        <option value="">— none (count rows) —</option>
                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </Select>
                </div>
                <div>
                    <Subheading>Aggregation</Subheading>
                    <Select className="mt-2" value={aggregation} onChange={(e) => setAggregation(e.target.value as Aggregation)} disabled={!yColumn}>
                        <option value="count">Count</option>
                        <option value="sum">Sum</option>
                    </Select>
                </div>
                <div>
                    <Subheading>Chart type</Subheading>
                    <Select className="mt-2" value={chartType} onChange={(e) => setChartType(e.target.value as ChartType)}>
                        <option value="bar">Bar</option>
                        <option value="line">Line</option>
                        <option value="pie">Pie</option>
                    </Select>
                </div>
                <div>
                    <Subheading>Group dates by</Subheading>
                    <Select className="mt-2" value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
                        <option value="day">Day</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                        <option value="year">Year</option>
                    </Select>
                    <Text className="mt-1 text-xs !text-zinc-500">Only applies if the X axis column contains dates.</Text>
                </div>
                <div className="flex items-end">
                    <Button className="w-full justify-center" onClick={handleGenerate} disabled={generating || !xColumn}>
                        {generating ? 'Generating…' : 'Generate chart'}
                    </Button>
                </div>
            </div>
            </Card>

            {chartData && chartData.length === 0 && (
                <Text className="py-10 text-center !text-zinc-500">No data to chart for that combination.</Text>
            )}

            {chartData && chartData.length > 0 && (
                <Card className="mt-6 h-96 w-full p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        {chartType === 'bar' ? (
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="value" fill={CHART_COLORS[0]} />
                            </BarChart>
                        ) : chartType === 'line' ? (
                            <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                <YAxis />
                                <Tooltip />
                                <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} />
                            </LineChart>
                        ) : (
                            <PieChart>
                                <Tooltip />
                                <Legend />
                                <Pie data={chartData} dataKey="value" nameKey="name" outerRadius={120} label>
                                    {chartData.map((_, i) => (
                                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                            </PieChart>
                        )}
                    </ResponsiveContainer>
                </Card>
            )}
            {isDateSeries && chartData && chartData.length > 0 && (
                <Text className="mt-2 text-xs !text-zinc-500">Grouped by {groupBy}.</Text>
            )}
        </div>
    );
};

export default AnalyticsChartPage;
